import React, { createContext, useContext, useState, useEffect } from "react";
import client from "@/api/client";

const AuthContext = createContext(null);

// ── RBAC-ready role registry ───────────────────────────────────────────────
// All roles available to all users in this phase (frontend-only).
// Future upgrade: add `user.roles[]` from backend and filter via canAccessRole().
export const ALL_ROLES = ["sme", "project", "corporate", "nbplc", "nirsal", "lagos_state", "fi", "states", "training", "nosak", "mtn", "lcfpi", "lcfpi_gov", "lcfpi_pm", "lcfpi_investor"];

export const ROLE_META = {
  sme:       { label: "SME",                  fullLabel: "Small & Medium Enterprise",    color: "#059669", textClass: "text-emerald-400"  },
  project:   { label: "Project",              fullLabel: "Project Developer",            color: "#3B82F6", textClass: "text-blue-400"     },
  corporate: { label: "Corporate",            fullLabel: "Corporate Intelligence",       color: "#6366F1", textClass: "text-indigo-400"   },
  nbplc:     { label: "Corporate",            fullLabel: "Nigeria Breweries Plc",        color: "#D97706", textClass: "text-amber-400"    },
  nirsal:    { label: "Corporate",            fullLabel: "NIRSAL Green Credit Program",  color: "#1e3a5f", textClass: "text-blue-400"     },
  nosak:      { label: 'Nosak Group', fullLabel: 'Nosak Group Enterprise Workspace', color: '#1e293b', textClass: 'text-slate-400' },
  lagos_state: { label: "Lagos State Government", fullLabel: "Lagos State Climate & Carbon Platform", color: "#059669", textClass: "text-emerald-400"  },
  fi:        { label: "Financial Institution",fullLabel: "Financial Institution",        color: "#F59E0B", textClass: "text-amber-400"    },
  states:    { label: "States",               fullLabel: "State Governance Workspace",   color: "#8B5CF6", textClass: "text-violet-400"   },
  training:  { label: "Climate Training",     fullLabel: "Climate Learning Academy",     color: "#10B981", textClass: "text-teal-400"     },
  mtn:       { label: "MTN Nigeria",          fullLabel: "MTN Nigeria Enterprise Climate Intelligence Platform", color: "#FFCB00", textClass: "text-yellow-400" },
  lcfpi:     { label: "LCFPI",               fullLabel: "Lagos Climate Finance Pipeline Initiative", color: "#1e40af", textClass: "text-blue-400" },
  lcfpi_gov: { label: "LCFPI — Government",  fullLabel: "LCFPI Government Dashboard", color: "#059669", textClass: "text-emerald-400" },
  lcfpi_pm:  { label: "LCFPI — Programme",   fullLabel: "LCFPI Programme Manager", color: "#1e40af", textClass: "text-blue-400" },
  lcfpi_investor: { label: "LCFPI — Investor", fullLabel: "LCFPI Investor & DFI Workspace", color: "#7c3aed", textClass: "text-violet-400" },
};

export const AuthProvider = ({ children }) => {
  const [user,        setUser]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [currentOrg,  setCurrentOrg]  = useState(null);

  // Active workspace role — persisted across sessions.
  // Admin role ALWAYS overrides any stale localStorage value.
  const [activeRole, setActiveRoleState] = useState(
    () => {
      const savedUser = JSON.parse(localStorage.getItem("decarboniq_user") || "null");
      if (savedUser?.role === "admin") return "admin";  // Admin is never overridden by stale state
      return localStorage.getItem("decarboniq_active_role") || "sme";
    }
  );

  const setActiveRole = (role) => {
    const _DEDICATED_ALL = ['lagos_state','nirsal','nosak','nbplc','mtn','lcfpi','lcfpi_gov','lcfpi_pm','lcfpi_investor'];
    if (!ALL_ROLES.includes(role) && !_DEDICATED_ALL.includes(role)) return;
    setActiveRoleState(role);
    localStorage.setItem("decarboniq_active_role", role);
    // Sync with backend (fire-and-forget — non-blocking)
    client.patch("/auth/me/role", { role }).catch(() => {});
  };

  // canAccessRole: hook point for future server-side RBAC.
  // Currently returns true for all roles. Upgrade by checking user.roles[].
  const canAccessRole = (_roleId) => true;

  useEffect(() => {
    const token    = localStorage.getItem("decarboniq_token");
    const savedUser= localStorage.getItem("decarboniq_user");
    const savedOrg = localStorage.getItem("decarboniq_org");
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        // Admin cold-boot: always force admin role, clear any stale workspace context
        if (parsedUser?.role === "admin") {
          setActiveRoleState("admin");
          localStorage.setItem("decarboniq_active_role", "admin");
          localStorage.setItem("decarboniq_workspace_path", "/admin");
        }
        if (savedOrg) setCurrentOrg(JSON.parse(savedOrg));
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await client.post("/auth/login", { email, password });
    const { token, user: userData } = res.data;

    // ── Flush all stale session state before applying new user's context ──────
    // Prevents cross-user route bleed-over when logging in on a shared browser.
    localStorage.removeItem("decarboniq_workspace_path");
    localStorage.removeItem("decarboniq_active_role");
    localStorage.removeItem("decarboniq_org");

    localStorage.setItem("decarboniq_token", token);
    localStorage.setItem("decarboniq_user", JSON.stringify(userData));

    // ── CRITICAL: Admin context must ALWAYS win ────────────────────────────
    if (userData.role === "admin") {
      setActiveRoleState("admin");
      localStorage.setItem("decarboniq_active_role", "admin");
      localStorage.setItem("decarboniq_workspace_path", "/admin");
    } else {
      // Use backend-provided routing context (v2 protocol)
      const routing = userData.routing || {};
      const landingRoute = routing.landing_route || userData.default_workspace_path;
      if (landingRoute) {
        // Store with user_id tag so we can validate staleness later
        const routeEntry = JSON.stringify({ route: landingRoute, user_id: userData.id });
        localStorage.setItem("decarboniq_workspace_path", routeEntry);
      }
      // Sync active_role from backend
      if (userData.active_role) {
        setActiveRoleState(userData.active_role);
        localStorage.setItem("decarboniq_active_role", userData.active_role);
      }
    }

    setUser(userData);
    try {
      const orgsRes = await client.get("/organizations");
      if (orgsRes.data.length > 0) {
        const org = orgsRes.data[0];
        setCurrentOrg(org);
        localStorage.setItem("decarboniq_org", JSON.stringify(org));
      }
    } catch {}
    return userData;
  };

  const register = async (email, password, firstName, lastName, extraData = {}) => {
    const res = await client.post("/auth/register", {
      email, password, first_name: firstName, last_name: lastName,
      ...extraData
    });
    const { token, user: userData } = res.data;
    localStorage.setItem("decarboniq_token", token);
    localStorage.setItem("decarboniq_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("decarboniq_token");
    localStorage.removeItem("decarboniq_user");
    localStorage.removeItem("decarboniq_org");
    // Keep active role preference on logout
    setUser(null);
    setCurrentOrg(null);
    window.location.href = "/login";
  };

  const switchOrg = (org) => {
    setCurrentOrg(org);
    localStorage.setItem("decarboniq_org", JSON.stringify(org));
  };

  return (
    <AuthContext.Provider value={{
      user, currentOrg, loading,
      login, register, logout, switchOrg,
      activeRole, setActiveRole, canAccessRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
